import { SecurityErrorLOFactory } from "./../../externalLogging/factories/SecurityErrorLOFactory";
import { ExternalLog } from "./../../externalLogging/ExternalLog";
import { ILoadingObject } from "./ILoadingObject";
import { Loader, URLLoader, URLRequest, Timer } from "@goodgamestudios/createjs-utilities"; // TODO: @HTML5 URLRequest will be handled as loader in js
import { Signal } from "signals.js";
import { getLogger } from "@goodgamestudios/logger";

import Event = createjs.Event;
import IOErrorEvent = createjs.IOErrorEvent;
import ProgressEvent = createjs.ProgressEvent;
import TimerEvent = createjs.TimerEvent;

const logger = getLogger("LoaderObject");

/**
 * <p>LoaderObject handles the loading progress of one single URLLoader or Loader</p>
 *
 * @author dkoehler
 */
export class LoaderObject implements ILoadingObject {
    /** loader is initialized but is not finished and not loading at the moment. */
    public static STATE_IDLE: number = 1;
    /** loading progress is initiated and first progress event was fired. */
    public static STATE_LOADING_PROGRESS_INITIATED: number = 2;
    /** object is now loading and data about filesize is now available. */
    public static STATE_LOADING_PROGRESS_STARTED: number = 3;
    /** object is now loaded. */
    public static STATE_LOADING_PROGRESS_FINISHED: number = 4;
    /** object is not loaded. has error. */
    public static STATE_LOADING_ERROR: number = 5;
    /** global state of this loader. */
    private _currentState: number;
    /** current state of this loader. */
    public get currentState(): number {
        return this._currentState;
    }

    /** returns true when the loader is finished */
    public get isLoadingFinished(): boolean {
        return this._currentState == LoaderObject.STATE_LOADING_PROGRESS_FINISHED;
    }

    private _loaderName: string;
    /** name of this loader object*/
    public get loaderName(): string {
        return this._loaderName;
    }

    private _loaderInstance: Object;
    /** refernce to loader instance. */
    public get loaderInstance(): Object {
        return this._loaderInstance;
    }

    private _totalBytes: number;
    /** size of bytes that must be loaded. logs error if loader not loading */
    public get totalBytes(): number {
        if (this._currentState == LoaderObject.STATE_LOADING_PROGRESS_STARTED || this._currentState == LoaderObject.STATE_LOADING_PROGRESS_FINISHED) {
            return this._totalBytes;
        }
        else {
            return 0;
        }
    }

    private _loadedBytes: number;
    /** size of bytes that are loaded.  logs error if loader not loading*/
    public get loadedBytes(): number {
        if (this._currentState == LoaderObject.STATE_LOADING_PROGRESS_STARTED || this._currentState == LoaderObject.STATE_LOADING_PROGRESS_FINISHED) {
            return this._loadedBytes;
        }
        else {
            return 0;
        }
    }

    /** @private signal that this object has finished loading. */
    private _loaderObjectFinished: Signal = new Signal();
    public get loaderObjectFinished(): Signal {
        return this._loaderObjectFinished;
    }

    /** @private signal that loading progress for this object is now starting. */
    /*internal*/
    loadingProgressStarted: Signal = new Signal();
    /*internal*/
    loadingError: Signal = new Signal();
    /** url request for this loader. */
    protected _request: URLRequest;
    /** callback that will be executed after finished loading */
    private _onCompleteCallback: Function;
    /** helper to calculate delta of loaded bytes. */
    private tempLoadedBytes: number;
    /** loaded bytes within one gametick. */
    private loadingProgressDelta: number;
    /** timer to check loadingProgressDelta within an interval. */
    private loadingProgressDeltaTimer: Timer = new Timer(1000);
    /** count of ticks, that loader is loading null bytes. */
    private loadingProgressDeltaNullTicks: number = 0;
    /** flag if errors of subloader should be thrown. */
    private subloaderErrorLogging: boolean = true;

    /**
     * creates a new loader-object that will be loaded by GoodgameLoader
     *
     * @param loader instance of loader
     * @param name unique name for this loader
     * @param request url-request object for this loader
     * @param onCompleteCallback callback that will be executed after finished loading
     */
    constructor(loader: Object, name: string, request: URLRequest, onCompleteCallback: Function = null) {
        this._loaderInstance = loader;
        this._loaderName = name;
        this._request = request;
        this._onCompleteCallback = onCompleteCallback !== null ? this.bindFunction(onCompleteCallback) : null;
        this._currentState = LoaderObject.STATE_IDLE;
        this.loadingProgressDeltaTimer.addEventListener(TimerEvent.TIMER, this.bindFunction(this.handleLoadingProgressDeltaTimerTick));
    }

    public dispose(): void {

        // TODO: @HTML5 - need a new logging system
        //LogChannels.loading.warn("start disposing loader-object " + this._loaderName);
        this._loaderObjectFinished.removeAll();
        this.loadingProgressStarted.removeAll();
        this._onCompleteCallback = null;
        this._loaderInstance = null;
        this.removeTimer();
    }

    /**
     * @private start loading this object
     */
    public startLoadingObject(_subloaderErrorLogging: boolean): void {
        // TODO: @HTML5 - need a new logging system
        //LogChannels.loading.debug("start loading object: " + this._loaderName);
        this.subloaderErrorLogging = _subloaderErrorLogging;
        this.loadingProgressDeltaTimer.start();
        if (this._loaderInstance instanceof Loader) {
            (this._loaderInstance as Loader).load(this._request);
        }
        else if (this._loaderInstance instanceof URLLoader) {
            (this._loaderInstance as URLLoader).load(this._request);
        }
        /* TODO: @HTML5 handle IOErrorEvent class
         else if (this._loaderInstance instanceof LoaderInfo) {
         // loader info is already loading
         }*/
    }

    /**
     * @private handle event
     */
    public onLoadProgress(event: ProgressEvent): void {
        // TODO: @HTML5 - no bytestotla/loaded on the ProgressEvent
        /*this._totalBytes = event.bytesTotal;
         this._loadedBytes = event.bytesLoaded;
         this.loadingProgressDelta = event.bytesLoaded - this.tempLoadedBytes;
         switch (this._currentState) {
         case LoaderObject.STATE_IDLE: {
         this._currentState = LoaderObject.STATE_LOADING_PROGRESS_INITIATED;
         break;
         }
         case LoaderObject.STATE_LOADING_PROGRESS_INITIATED: {
         this._currentState = LoaderObject.STATE_LOADING_PROGRESS_STARTED;
         this.loadingProgressStarted.dispatch(this._loaderName);
         break;
         }
         default: {
         break;
         }
         }
         this.tempLoadedBytes = event.bytesLoaded;*/
    };

    private handleLoadingProgressDeltaTimerTick(event: TimerEvent): void {
        if (this.loadingProgressDelta <= 0) {
            this.loadingProgressDeltaNullTicks++;
        }
        else {
            this.loadingProgressDeltaNullTicks = 0;
        }
        if (this.loadingProgressDeltaNullTicks >= 5) {
            if (this.subloaderErrorLogging) {
                //ExternalLog.logger.log( new NullByteLoadingLO( this._loadedBytes, this._totalBytes, this._currentState, this._loaderName, this._request.url ), "LoaderObject.as" );
            }
        }
    };

    /**
     * @private handle event
     */
    // TODO: @HTML5 handle SecurityErrorEvent class, probably not needed for js
    // public handleLoaderSecurityError = (event: SecurityErrorEvent) => {
    public handleLoaderSecurityError(event: any): void {
        if (this.subloaderErrorLogging) {
            ExternalLog.logger.log(new SecurityErrorLOFactory(SecurityErrorLOFactory.GENERAL_LOADER_SECURITY_ERROR, event.text, this._request.url));
        }
        // TODO: @HTML5 - need a new logging system
        //LogChannels.loading.fatal(event.text);
        this._currentState = LoaderObject.STATE_LOADING_ERROR;
        this.loadingError.dispatch(this._loaderName, event);
        this.loaderObjectFinished.dispatch(this._loaderName);
        this.removeTimer();
    };

    /**
     * @private handle event
     */
    public handleLoaderIOError(event: IOErrorEvent): void {
        if (this.subloaderErrorLogging) {
            // TODO: @HTML5 handle IOErrorEvent class
            // ExternalLog.logger.log(new IOErrorLOFactory(IOErrorLOFactory.GENERAL_LOADER_IO_ERROR, event.text, this._request.url));
        }
        // TODO: @HTML5 handle IOErrorEvent class
        // LogChannels.loading.fatal(event.text);
        this._currentState = LoaderObject.STATE_LOADING_ERROR;
        this.loadingError.dispatch(this._loaderName, event);
        this.loaderObjectFinished.dispatch(this._loaderName);
        this.removeTimer();
    };

    /**
     * @private handle event
     */
    public onComplete(event: Event): void {
        // TODO: @HTML5 - need a new logging system
        //LogChannels.loading.debug("loader: " + this._loaderName + " has finished");
        this._currentState = LoaderObject.STATE_LOADING_PROGRESS_FINISHED;
        this._loaderObjectFinished.dispatch(this._loaderName);
        this.removeTimer();
        if (this._onCompleteCallback != null) {
            try {
                this._onCompleteCallback(this.loaderName); // fuu wtf !
            }
            catch (error) {
                logger.error(error)
                //  this._onCompleteCallback(); this seriously makes no sense.  and causes unpredictable behvaiour if an error happens down the chain of the callback
            }
        }
    };

    private removeTimer(): void {
        if (this.loadingProgressDeltaTimer) {
            this.loadingProgressDeltaTimer.stop();
            this.loadingProgressDeltaTimer.removeEventListener(TimerEvent.TIMER, this.bindFunction(this.handleLoadingProgressDeltaTimerTick));
        }
    }
}
